import maputilities from '@smarttransit/map-utilities'

export function isAgentTopUpPassengerTransaction (transaction) {
  if (!transaction || transaction.logType !== 'purchased_credit') return false
  return Boolean(transaction.transactionLogReferenceId)
}

export function getAgentTopUpTransactionLogId (transaction) {
  if (!transaction || transaction.logType !== 'purchased_credit') return null
  if (transaction.transactionLogReferenceId) return transaction.transactionLogReferenceId
  if (transaction.logMetadata && transaction.logMetadata.agentTransactionLogId) {
    return transaction.logMetadata.agentTransactionLogId
  }
  return null
}

export function formatAgentDetailsLabel (stUser) {
  if (!stUser) return ''
  return '<code>' + JSON.stringify({
    phoneNumber: stUser.phone || stUser.phoneNumber || null,
    email: stUser.email || null,
    firstName: stUser.firstName,
    lastName: stUser.lastName
  }, null, 2) + '</code>'
}

export function getAgentStUserFromJoinedLog (agentLog) {
  if (!agentLog || !agentLog.rawAgentProfile) return null
  return agentLog.rawAgentProfile.stUser || null
}

export function shouldShowDateRecorded (transaction) {
  if (!transaction || !transaction.dateRecorded) return false
  if (transaction.dateRecordedLabel && transaction.dateCreatedLabel) {
    return transaction.dateRecordedLabel !== transaction.dateCreatedLabel
  }
  if (!transaction.dateCreated) return true
  return String(transaction.dateRecorded) !== String(transaction.dateCreated)
}

const RELATED_USER_ACTION_FIELDS = [
  { key: 'requestedByStUserId', label: 'Requested by' },
  { key: 'stUserId', label: 'User' },
  { key: 'expiredByStUserId', label: 'Expired by' }
]

export function getRelatedTransactionUserActions (logMetadata) {
  if (!logMetadata) return []
  const actions = []
  for (let i = 0; i < RELATED_USER_ACTION_FIELDS.length; i++) {
    const field = RELATED_USER_ACTION_FIELDS[i]
    if (logMetadata[field.key]) {
      actions.push({ label: field.label, stUserId: logMetadata[field.key] })
    }
  }
  return actions
}

function appendDetailRow (rows, label, value) {
  if (value === null || value === undefined || value === '') return
  rows.push({ label: label, value: value })
}

export function getEventDetailRows (event) {
  if (!event) return []
  const rows = []
  appendDetailRow(rows, 'Event name', event.eventName)
  appendDetailRow(rows, 'Event ID', event.eventId)
  appendDetailRow(rows, 'Event date', event.eventDateLabel || event.eventDate)
  appendDetailRow(rows, 'Pickup time', event.pickupTime)
  appendDetailRow(rows, 'Flat rate fee', event.flatRateFee)
  appendDetailRow(rows, 'Pickup stop', event.busStopLabel)
  appendDetailRow(rows, 'Origin', event.originLabel)
  appendDetailRow(rows, 'Destination', event.destinationLabel)
  if (event.isReturnEventTicket === true) appendDetailRow(rows, 'Return trip', 'Yes')
  if (event.isReturnEventTicket === false) appendDetailRow(rows, 'Return trip', 'No')
  return rows
}

export function getPersonDetailRows (person) {
  if (!person) return []
  const rows = []
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ')
  appendDetailRow(rows, 'Name', name)
  appendDetailRow(rows, 'Phone', person.phoneNumber || person.phone)
  appendDetailRow(rows, 'Email', person.email)
  return rows
}

export function toViewedPerson (stUser) {
  if (!stUser) return null
  return {
    phoneNumber: stUser.phoneNumber || stUser.phone || null,
    email: stUser.email || null,
    firstName: stUser.firstName,
    lastName: stUser.lastName
  }
}

export function getTicketSurchargeAmount (transaction) {
  const surcharges = transaction && transaction.logMetadata && transaction.logMetadata.surcharges
  const entry = surcharges && surcharges.beaconMaintenanceFee
  const amount = entry ? Number(entry.amount) : 0
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

export function getTicketSurchargeRows (transaction, formatAmount) {
  const fee = getTicketSurchargeAmount(transaction)
  if (!fee) return []
  const total = Math.abs(Number(transaction.amount) || 0)
  const fare = Math.max(0, total - fee)
  const format = typeof formatAmount === 'function' ? formatAmount : function (value) { return String(value) }
  return [
    { label: 'Fare (excl. surcharges)', value: format(fare) },
    { label: 'Beacon maintenance fee', value: format(fee) }
  ]
}

export function getTicketFareFeeLabel (transaction, formatAmount) {
  const fee = getTicketSurchargeAmount(transaction)
  if (!fee) return ''
  const fare = Math.max(0, Math.abs(Number(transaction.amount) || 0) - fee)
  const format = typeof formatAmount === 'function' ? formatAmount : function (value) { return String(value) }
  return format(fare) + ' fare + ' + format(fee) + ' fee'
}

export function getRefundSurchargeHint (tickets, formatAmount) {
  const list = Array.isArray(tickets) ? tickets : (tickets ? [tickets] : [])
  let fee = 0
  let fare = 0
  for (let i = 0; i < list.length; i++) {
    const ticketFee = getTicketSurchargeAmount(list[i])
    if (!ticketFee) continue
    fee += ticketFee
    fare += Math.max(0, Math.abs(Number(list[i].amount) || 0) - ticketFee)
  }
  if (!fee) return ''
  const format = typeof formatAmount === 'function' ? formatAmount : function (value) { return String(value) }
  return 'Includes a beacon maintenance fee of ' + format(fee) + '. It is refunded only when the full fare is refunded. Entering the fare (' + format(fare) + ') or the full amount (' + format(fare + fee) + ') refunds everything.'
}

export function getExpireNowSurchargeHint (transaction) {
  if (!getTicketSurchargeAmount(transaction)) return ''
  return 'Refunds everything remaining, including the beacon maintenance fee.'
}

export function getReconfirmCodeLabel (logMetadata) {
  if (!logMetadata || !logMetadata.reconfirmCode) return ''
  return String(logMetadata.reconfirmCode).toUpperCase()
}

export function formatTripRouteLabel (trip, stRouteLabel) {
  if (stRouteLabel) return stRouteLabel
  if (!trip) return ''
  return maputilities.joinRouteLabel(trip.originLabel, trip.destinationLabel)
}
